import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react';
import React from 'react';
import {
  Anchor,
  Button,
  Card,
  CardContent,
  Grid,
  GridColumn,
  Heading,
  Icon,
  ModalProvider,
  ModalTrigger,
  Notice,
  Separator,
  Skeleton,
  SkeletonItem,
  Text,
  scrollToElement,
} from 'telia-front-react';
import Config from '../Config';
import { Passkey } from '../stores/PasskeyStore';
import useStores from '../stores/UseStores';
import { FetchStatus } from '../util/ApiUtil';
import { useIsMobileHook } from '../util/hooks/useIsMobile';
import LoginMethodsAccordionItem from './LoginMethodsAccordionItem';
import PasskeyDeleteConfirmationModal from './PasskeyDeleteConfirmationModal';

const PasskeyAccordionItem = observer((): React.JSX.Element => {
  const {
    translateStore: { translate },
    passkeyStore: { passkeys, passkeysStatus, fetchPasskeys, handleAddNewPasskeyOnAccountPage, handleDeletePasskeyOnAccountPage, status, deleteStatus },
    uiStore: { isMobileDevice },
    accountInfoStore: { showPasskey },
  } = useStores();
  const { isMobile } = useIsMobileHook();
  const canAddPasskey = showPasskey && isMobileDevice;

  React.useEffect(() => {
    fetchPasskeys();
  }, []);

  React.useEffect(() => {
    if (status === FetchStatus.FETCHED) {
      scrollToElement('passkey.register.success', undefined, 10, true);
    } else if (status === FetchStatus.ERROR) {
      scrollToElement('passkey.register.error', undefined, 10, true);
    }
  }, [status]);

  React.useEffect(() => {
    if (deleteStatus === FetchStatus.FETCHED) {
      scrollToElement('passkey.delete.success', undefined, 10, true);
    } else if (deleteStatus === FetchStatus.ERROR) {
      scrollToElement('passkey.delete.error', undefined, 10, true);
    }
  }, [deleteStatus]);

  const renderCard = (passkey: Passkey) => {
    const formattedDate = format(parseISO(passkey.creationDate), 'dd.MM.yyyy');
    return (
      <Card padding={'small'}>
        <CardContent background={'neutral-bg-level-1'}>
          <Grid align={['middle-xs']} equalHeight gutter="smedium" noWrap={['xs']}>
            <GridColumn width={['min-xs']}>
              <Icon modifiers="#mobile" size="sm" />
            </GridColumn>
            <GridColumn width={['max-xs']}>
              <Grid align={['middle-xs']} gutter="small" noWrap={['lg']}>
                <GridColumn width={['max-lg']}>
                  <Text>
                    <Heading element="h3" className="h5 text-bold">
                      {passkey.credentialName}
                    </Heading>
                    <p className="text-small text__margin-none">{translate('account.passkey.usage.info', { createdAt: formattedDate })}</p>
                  </Text>
                </GridColumn>
                <GridColumn width={['min-xs']}>
                  <ModalTrigger>
                    <Button type="withdrawal" text={translate('account.passkey.remove')} size="small" variant="secondary" iconLeft="#trashcan-empty" />
                  </ModalTrigger>
                </GridColumn>
              </Grid>
            </GridColumn>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderPasskeyOrAddButtonOrNotice = () => {
    if ([FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(passkeysStatus)) {
      return (
        <GridColumn width={['xs-12']}>
          <Skeleton label={translate('skeleton.loading')}>
            <SkeletonItem width="100%" height={isMobile ? '78px' : '134px'} />
          </Skeleton>
        </GridColumn>
      );
    }
    if (passkeysStatus === FetchStatus.ERROR) {
      return (
        <GridColumn width={['xs-12']}>
          <Notice type="attention">{translate('account.passkey.load.error')}</Notice>
        </GridColumn>
      );
    }
    if (passkeys?.length) {
      return (
        <GridColumn width={['xs-12']}>
          {passkeys.map((passkey, index) => (
            <React.Fragment key={`passkey-${index}`}>
              <ModalProvider>
                {renderCard(passkey)}
                <PasskeyDeleteConfirmationModal onConfirm={() => handleDeletePasskeyOnAccountPage(passkey.credentialId)} />
              </ModalProvider>
              <Separator spacing="xxsmall" color="invisible" />
            </React.Fragment>
          ))}
          <Notice type="info" size="small">
            {translate('account.passkey.restriction')}
          </Notice>
        </GridColumn>
      );
    }
    if (canAddPasskey) {
      return (
        <GridColumn width={['min-xs']}>
          <Button type="expressive" text={translate('account.passkey.add')} iconLeft="#plus" onClick={handleAddNewPasskeyOnAccountPage} />
        </GridColumn>
      );
    }
    return (
      <GridColumn width={['xs-12']}>
        <Notice type="attention">
          {translate(`account.passkey.add.notAvailable${isMobileDevice ? '.mobile' : ''}`)}{' '}
          <Anchor url={translate('account.passkey.add.notAvailable.seeMore.url', { baseUrl: Config.teliaWebUrl })}>
            {translate('account.passkey.add.notAvailable.seeMore')}
          </Anchor>
        </Notice>
      </GridColumn>
    );
  };

  const renderAccordionContent = () => {
    return (
      <Grid align={['middle-xs']}>
        {renderPasskeyOrAddButtonOrNotice()}
        <GridColumn width={['xs-12', 'sm-3']}>
          <Button
            text={translate('account.passkey.explanation.button')}
            url={translate('account.passkey.explanation.button.url')}
            link
            external
            target="_blank"
          />
        </GridColumn>
      </Grid>
    );
  };

  return (
    <LoginMethodsAccordionItem
      id={'passkey'}
      title={translate('account.passkey.accordion.label')}
      icon={'#key'}
      subTitle={translate('account.passkey.accordion.description')}
    >
      {renderAccordionContent()}
    </LoginMethodsAccordionItem>
  );
});

export default PasskeyAccordionItem;
